import { apiFetch } from "@/api/client";

/**
 * Catalog read API (Phase 3, ADR-0040 read surfaces).
 *
 * Types mirror `onboarding_api.models.api` (CatalogSummary / CatalogListResponse).
 * Hand-written rather than generated so the Phase-3 slice is not blocked on
 * `npm run generate-types` (which needs the backend running); regen can replace
 * these later without a call-site change.
 */

/** Three-tier scoping per ADR-0033 (mirrors the backend `CatalogTier`). */
export type CatalogTier = "global" | "tenant" | "branch";

export interface CatalogSummary {
  tier: CatalogTier;
  /** Present for tenant/branch tiers; null for the global catalog. */
  org_id: string | null;
  /** Present only for the branch tier. */
  branch_id: string | null;
  biomarker_count: number;
}

export interface CatalogListResponse {
  catalogs: CatalogSummary[];
}

/** `GET /api/v1/catalogs` — enumerate the catalogs visible to the principal. */
export function listCatalogs(): Promise<CatalogListResponse> {
  return apiFetch<CatalogListResponse>("/api/v1/catalogs");
}

/**
 * A biomarker as rendered by the catalog detail table — the summary subset
 * (`BiomarkerDetail` carries the rest for the biomarker view). The API returns
 * full `BiomarkerDef` objects; TypeScript structural typing ignores fields we
 * don't declare. A non-empty `members` marks a parent grouper (ADR-0063).
 */
export interface BiomarkerRow {
  name: string;
  display: string;
  abbreviation: string | null;
  panel_name: string | null;
  members: string[];
}

export interface BiomarkerUnit {
  code: string;
  is_canonical: boolean;
}

export interface BiomarkerCodes {
  loinc: string | null;
  snomed: string | null;
  secondary_loinc: string[];
}

export interface ClinicalContext {
  combined_what_why: string | null;
  how_template: string | null;
  how_custom: string | null;
  special_notes: string | null;
  biomarker_statement: string | null;
}

/** The fuller biomarker shape the read-only biomarker view renders. */
export interface BiomarkerDetail extends BiomarkerRow {
  variable_name: string | null;
  sample_type: string | null;
  coverage_type: string | null;
  allowed_categories: string[];
  calculated_from: string[];
  codes: BiomarkerCodes;
  units: BiomarkerUnit[];
  clinical_context: ClinicalContext;
}

export interface CatalogResponse {
  tier: CatalogTier;
  org_id: string | null;
  branch_id: string | null;
  biomarkers: BiomarkerDetail[];
}

/** `GET /api/v1/catalogs/{org}` — load one tenant catalog. */
export function loadCatalog(org: string): Promise<CatalogResponse> {
  return apiFetch<CatalogResponse>(`/api/v1/catalogs/${encodeURIComponent(org)}`);
}

/** `GET /api/v1/catalogs/{org}/branch/{branch}` — load one branch catalog. */
export function loadBranchCatalog(org: string, branch: string): Promise<CatalogResponse> {
  return apiFetch<CatalogResponse>(
    `/api/v1/catalogs/${encodeURIComponent(org)}/branch/${encodeURIComponent(branch)}`,
  );
}

/** The opened merge request (ADR-0040 write path). */
export interface SubmitResult {
  mr_url: string;
  branch_name: string;
}

/**
 * `POST /api/v1/catalogs/{org}/biomarkers` — open a GitLab MR adding a biomarker
 * to the tenant catalog. The live catalog is not mutated (PR-as-the-gate). 503
 * when MR submission isn't enabled/provisioned on the backend.
 */
export function addBiomarker(
  org: string,
  biomarker: Record<string, unknown>,
): Promise<SubmitResult> {
  return apiFetch<SubmitResult>(`/api/v1/catalogs/${encodeURIComponent(org)}/biomarkers`, {
    method: "POST",
    body: JSON.stringify(biomarker),
  });
}

export interface ValidationIssue {
  message: string;
  biomarker: string | null;
}

export interface ValidationResult {
  is_valid: boolean;
  errors: ValidationIssue[];
  warnings: ValidationIssue[];
}

/**
 * `POST /api/v1/catalogs/{org}/validate` — dry-run: catalog-level validation of a
 * biomarker (duplicate name, tier invariants). No MR, no write.
 */
export function validateBiomarker(
  org: string,
  biomarker: Record<string, unknown>,
): Promise<ValidationResult> {
  return apiFetch<ValidationResult>(`/api/v1/catalogs/${encodeURIComponent(org)}/validate`, {
    method: "POST",
    body: JSON.stringify(biomarker),
  });
}

export interface SpreadsheetRowError {
  sheet: string;
  row_index: number;
  biomarker: string | null;
  message: string;
}

export interface SpreadsheetPreview {
  imported: string[];
  skipped: string[];
  errors: SpreadsheetRowError[];
  validation: ValidationResult;
  is_clean: boolean;
}

/**
 * `POST /api/v1/catalogs/{org}/spreadsheet` — dry-run: parse an uploaded
 * clinical-content spreadsheet and preview what it contains. No MR, no write.
 */
export function previewSpreadsheet(org: string, file: File): Promise<SpreadsheetPreview> {
  const form = new FormData();
  form.append("file", file);
  return apiFetch<SpreadsheetPreview>(`/api/v1/catalogs/${encodeURIComponent(org)}/spreadsheet`, {
    method: "POST",
    body: form,
  });
}

/**
 * ADR-0064 Phase 1 per-organization presentation config (docs#150). Mirrors
 * `smart_reports_models.ClientConfig`. Presentation only: none of these fields touches a
 * value, a severity or which rules fire.
 */
export interface BiomarkerDisplayRule {
  /** Canonical symbol, the join key. Never renamed. */
  symbol: string;
  /** False hides the biomarker (and its insights) from this client's reports. */
  include?: boolean;
  /** Hide the biomarker when it has no resulted value. */
  drop_if_empty?: boolean;
  /** Sort key within its panel. Unset rows follow the ordered ones. */
  order?: number | null;
  /** The name this client's patients see. */
  relabel?: string | null;
}

export interface PanelRule {
  /** Canonical panel name, as the catalog's `panel_name`. */
  name: string;
  /** False drops the panel heading. It does NOT hide the panel's biomarkers. */
  include?: boolean;
  order?: number | null;
  relabel?: string | null;
}

export interface ClientConfig {
  organization_id: string;
  biomarkers: BiomarkerDisplayRule[];
  panels: PanelRule[];
  /**
   * Gate B lab settings (schema_version 2). The editor does not edit them, it carries them
   * from the served config so an edit to a relabel never deletes them. Unset means the
   * lab takes the default (automatic, M, en). A config that cannot be read means review.
   */
  approval_mode?: "review" | "automatic" | null;
  content_span?: "XS" | "S" | "M" | "L" | null;
  default_language?: "en" | "ar" | "de" | null;
  /** The lab's name for patients ({lab}) and its IANA timezone, carried the same way. */
  display_name?: string | null;
  timezone?: string | null;
  schema_version?: number;
}

/** `config` is null when the org has no config, so every biomarker is shown. */
export interface ClientConfigRead {
  organization_id: string;
  config: ClientConfig | null;
}

/**
 * A validate result plus the file the MR would commit. `current_yaml` is the SERVED config
 * ("" = none yet, null = could not be loaded, and a warning says why). `diff` is null exactly
 * when `current_yaml` is.
 */
export interface ClientConfigValidation extends ValidationResult {
  proposed_yaml: string;
  current_yaml: string | null;
  diff: string | null;
}

/** `GET /api/v1/catalogs/{org}/config` (catalog:read). `org` is a slug or an org id. */
export function getClientConfig(org: string): Promise<ClientConfigRead> {
  return apiFetch<ClientConfigRead>(`/api/v1/catalogs/${encodeURIComponent(org)}/config`);
}

/** `POST /api/v1/catalogs/{org}/config/validate` (catalog:author). Dry run, no MR. */
export function validateClientConfig(
  org: string,
  config: ClientConfig,
): Promise<ClientConfigValidation> {
  return apiFetch<ClientConfigValidation>(
    `/api/v1/catalogs/${encodeURIComponent(org)}/config/validate`,
    { method: "POST", body: JSON.stringify(config) },
  );
}

/** `POST /api/v1/catalogs/{org}/config` (catalog:author). Opens a GitLab MR, 503 when off. */
export function submitClientConfig(org: string, config: ClientConfig): Promise<SubmitResult> {
  return apiFetch<SubmitResult>(`/api/v1/catalogs/${encodeURIComponent(org)}/config`, {
    method: "POST",
    body: JSON.stringify(config),
  });
}
